import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import {
  SearchSelectOption,
  SearchSelect,
} from '@app/components/core/input/searchSelect/SearchSelect'
import { useClient } from '@based/sdk/react'
import { Style } from 'inlines'

export type RegionSelectorProps = {
  label?: string
  labelStyle?: Style
  required?: boolean
  errorMessage?: string
  region: SearchSelectOption
  setRegion: (region: SearchSelectOption) => void
}

export const RegionSelector = ({
  label,
  labelStyle,
  required,
  errorMessage,
  region,
  setRegion,
}: RegionSelectorProps) => {
  const translations = useTranslations().app.common

  const [regionQuery, setRegionQuery] = useState(region.value)
  const [regionOptions, setRegionOptions] = useState<SearchSelectOption[]>([])
  const [renderNotFoundMessage, setRenderNotFoundMessage] = useState(false)

  const client = useClient()
  useEffect(() => {
    if (regionQuery === region.value) {
      return
    }
    const close = client
      .query('suggestions-region', {
        searchTerm: regionQuery,
        top: 7,
      })
      .subscribe((r) => {
        const result: SearchSelectOption[] = r.map(
          (m: { id: any; name: any }) => ({
            key: m.id,
            value: m.name,
          }),
        )
        if (
          result.length === 1 &&
          result[0].key === region.key &&
          result[0].value === regionQuery
        ) {
          setRegionOptions([])
          setRenderNotFoundMessage(false)
        } else if (result.length === 0) {
          setRegionOptions([])
          setRenderNotFoundMessage(true)
        } else {
          setRegionOptions(result)
          setRenderNotFoundMessage(false)
        }
      })
    return () => close()
  }, [client, regionQuery, region])

  useEffect(() => {
    setRegionQuery(region.value)
    setRegionOptions([])
  }, [region])

  const selectLocationOption = useCallback(
    (option: SearchSelectOption) => {
      setRegionQuery(option.value)
      setRegion(option)
      setRegionOptions([])
      setRenderNotFoundMessage(false)
    },
    [setRegion],
  )

  const resetLocationSelection = useCallback(() => {
    setRegionQuery(region.value)
    setRegionOptions([])
  }, [region])

  return (
    <SearchSelect
      label={label ?? translations.region}
      labelStyle={labelStyle}
      required={required}
      errorMessage={errorMessage}
      value={regionQuery}
      setValue={setRegionQuery}
      selectOption={selectLocationOption}
      abandonSelection={resetLocationSelection}
      options={regionOptions}
      noResultsMessage={{
        display: renderNotFoundMessage,
        message: translations.regionNotFoundMessage,
      }}
    />
  )
}
